import React, { useState, useEffect } from 'react';
import { Card, Row, Col, DatePicker, Select, Statistic, Progress } from 'antd';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { VideoCameraOutlined, EyeOutlined, LikeOutlined, MessageOutlined } from '@ant-design/icons';
import { getOverview, getPublishRate, getVideoStats } from '../services/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const DataStats = () => {
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [publishRate, setPublishRate] = useState(null);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, 'day'),
    dayjs()
  ]);

  useEffect(() => {
    loadStats();
  }, [dateRange]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const [overviewRes, rateRes] = await Promise.all([
        getOverview({
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD')
        }),
        getPublishRate({
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD')
        })
      ]);

      if (overviewRes.success) {
        setOverview(overviewRes.data);
      }
      if (rateRes.success) {
        setPublishRate(rateRes.data);
      }
    } catch (error) {
      console.error('加载数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 播放量趋势图配置
  const playTrendOption = {
    title: { text: '播放量趋势', left: 'center' },
    tooltip: { 
      trigger: 'axis',
      formatter: '{b}<br />播放量：{c}'
    },
    xAxis: {
      type: 'category',
      data: ['4-12', '4-13', '4-14', '4-15', '4-16', '4-17', '4-18']
    },
    yAxis: { type: 'value' },
    series: [{
      data: [820, 932, 901, 934, 1290, 1330, 1320],
      type: 'line',
      smooth: true,
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: 'rgba(24,144,255,0.5)' },
          { offset: 1, color: 'rgba(24,144,255,0.1)' }
        ])
      },
      itemStyle: { color: '#1890ff' }
    }]
  };

  // 互动数据对比图
  const engagementOption = {
    title: { text: '互动数据对比', left: 'center' },
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { top: 'bottom' },
    xAxis: {
      type: 'category',
      data: ['美食探索者', '知识分享站', '生活日记', '科技前沿', '时尚搭配']
    },
    yAxis: { type: 'value' },
    series: [
      {
        name: '点赞数',
        type: 'bar',
        data: [2560, 1280, 890, 620, 450],
        itemStyle: { color: '#ff4d4f' }
      },
      {
        name: '评论数',
        type: 'bar',
        data: [1280, 640, 320, 180, 120],
        itemStyle: { color: '#52c41a' }
      },
      {
        name: '分享数',
        type: 'bar',
        data: [640, 320, 160, 90, 60],
        itemStyle: { color: '#722ed1' }
      }
    ]
  };

  // 发布成功率仪表盘
  const gaugeOption = {
    series: [{
      type: 'gauge',
      progress: { show: true },
      axisLine: { lineStyle: { width: 10 } },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: { show: false },
      detail: {
        valueAnimation: true,
        formatter: '{value}%',
        fontSize: 30,
        offsetCenter: [0, '70%']
      },
      data: [{ value: publishRate?.successRate || 0 }]
    }]
  };

  // 账号数据对比
  const accountCompareOption = {
    title: { text: '账号数据对比', left: 'center' },
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { top: 'bottom' },
    xAxis: {
      type: 'category',
      data: overview?.accounts ? ['账号 1', '账号 2', '账号 3', '账号 4'] : []
    },
    yAxis: { type: 'value' },
    series: [
      {
        name: '粉丝数',
        type: 'bar',
        data: overview?.accounts ? [128000, 85000, 62000, 45000] : [],
        itemStyle: { color: '#1890ff' }
      },
      {
        name: '获赞数',
        type: 'bar',
        data: overview?.accounts ? [2560000, 1280000, 890000, 620000] : [],
        itemStyle: { color: '#faad14' }
      }
    ]
  };

  return (
    <div>
      {/* 日期选择 */}
      <Card style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <h2 style={{ margin: 0 }}>数据统计</h2>
          </Col>
          <Col>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
            />
          </Col>
        </Row>
      </Card>

      {/* 核心指标 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总视频数"
              value={overview?.videos?.totalVideos || 0}
              prefix={<VideoCameraOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="总播放量"
              value={overview?.videos?.totalPlays || 0}
              prefix={<EyeOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="总点赞数"
              value={overview?.videos?.totalLikes || 0}
              prefix={<LikeOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="总评论数"
              value={overview?.videos?.totalComments || 0}
              prefix={<MessageOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 发布成功率 */}
      {publishRate && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Card title="发布成功率">
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <ReactECharts option={gaugeOption} style={{ height: 200 }} />
              </div>
              <div style={{ textAlign: 'center' }}>
                <div>成功：{publishRate.success} | 失败：{publishRate.failed} | 总计：{publishRate.total}</div>
              </div>
            </Card>
          </Col>
          <Col span={16}>
            <Card title="账号粉丝分布">
              <ReactECharts option={accountCompareOption} style={{ height: 200 }} />
            </Card>
          </Col>
        </Row>
      )}

      {/* 图表展示 */}
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Card>
            <ReactECharts option={playTrendOption} style={{ height: 300 }} />
          </Card>
        </Col>
        <Col span={12}>
          <Card>
            <ReactECharts option={engagementOption} style={{ height: 300 }} />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DataStats;
